// S1 (docs/27 §4): pure boot-payload → per-request SubsStoreState ingestion.
//
// This module converts the legacy nextjs=1 boot payload (the two hidden JSON
// spans) — plus optional per-section subs spans for folder/tab routes — into a
// renderable SubsStoreState WITHOUT touching the treeStore singleton, the
// runtime, or any browser global. The server route host uses it to compute a
// per-request initial state for SSR; the client island seeds the treeStore
// singleton from the exact same payload string, so the hydration render
// matches the server HTML by construction.
//
// It intentionally produces the pre-Stage-2d "store fallback" shape
// (legacyData.msges.subs + subs_tree + renderProjection.rootIds), which every
// selector still supports as the SSR-fallback path (docs/24 §13.7). After the
// legacy runtime boots and adopts the mirrored payload, projection sync
// replaces this state through the normal legacy-driven flow.
//
// PURITY RULES: no clock, nondeterministic value, or window/document/runtime
// access — the same payload string must always produce the same state.

import { extractPayloadSpanJson } from './payloadJson'
import type { AuxiliaryCollectionMode } from '../contracts/deskNavigation'
import type {
  ChromeElState,
  LegacyDataContainerKind,
  LegacyDataContainerState,
  SubsSection,
  SubsStoreState,
} from './types'
import type { PooClientProjection } from '../../contracts/poo'
import {
  mergeLegacyChildrenMaps,
  projectLegacySubsTree,
} from './legacyTree'

export const LEGACY_DATA_CONTAINER_IDS: Record<LegacyDataContainerKind, LegacyDataContainerState['id']> = {
  init: 'mcss-init_data-cssm',
  msges: 'mcss-msges_data-cssm',
  msgesDataJson: 'mcss-msges_data_json-cssm',
}

// Entity decoding lives in payloadJson.ts and runs AFTER the JSON parse — see
// the header there for why the container must never be decoded as a string.
export { extractPayloadSpanJson }

function parseJson(value: string | null): any {
  if (!value) return undefined
  try {
    return JSON.parse(value)
  } catch {
    return undefined
  }
}

export type DeskBootMode = 'msg' | 'answers' | 'submsges' | 'attachments' | 'favs' | 'drafts' | 'trash'

export interface BootSectionSpan {
  /** A load_more_subs-style data-span response for one folder/tab section. */
  html: string
  section: SubsSection
}

export interface BootCollectionSpan extends BootSectionSpan {
  mode: AuxiliaryCollectionMode
}

export interface PayloadToStateOptions {
  mode?: DeskBootMode
  activeSection?: SubsSection
  sectionSpans?: BootSectionSpan[]
  collectionSpans?: BootCollectionSpan[]
  pooProtocol?: PooClientProjection
}

function emptySubsStoreState(): SubsStoreState {
  return {
    nodesById: {},
    nodesByElId: {},
    htmlIdToNodeId: {},
    childrenMap: {},
    activeIds: [],
    syncVersion: 0,
    visibilityMap: {},
    subStates: {},
    nextNodeSeq: 1,
    templateData: { text: {}, attrs: {}, elementToText: {} },
    legacyData: {},
    renderProjection: {
      rootIds: { answers: [], submsges: [] },
      currentPage: {},
      mode: 'msg',
      activeSection: 'answers',
    },
    chromeByContract: {},
    subsLoading: null,
    subChildrenHidden: {},
  }
}

function chromeSeed(visible: boolean | undefined): ChromeElState {
  return {
    mounted: false,
    attrs: {},
    visible,
    classes: {},
    styles: {},
    props: {},
  }
}

// Mirrors MsgDeskApp's forceWorkspaceVisibility() decision table so the SSR
// markup starts in the state the post-boot chrome projection would produce.
export function chromeSeedsForMode(mode: DeskBootMode): Record<string, ChromeElState> {
  const mainVisible = mode === 'msg' || mode === 'answers' || mode === 'submsges'
  return {
    'workspace.main': chromeSeed(mainVisible),
    'workspace.editor': chromeSeed(false),
    'workspace.attachments': chromeSeed(mode === 'attachments'),
    'workspace.favs': chromeSeed(mode === 'favs'),
    'workspace.trash': chromeSeed(mode === 'trash'),
    'workspace.drafts': chromeSeed(mode === 'drafts'),
    'main.root': chromeSeed(mode === 'msg'),
    'subs.toolbar': chromeSeed(mode === 'msg'),
    'subs.answers.front': chromeSeed(mainVisible && mode !== 'submsges'),
    'subs.submsges.front': chromeSeed(mainVisible && mode !== 'answers'),
    'editor.root': chromeSeed(false),
  }
}

function containerState(
  kind: LegacyDataContainerKind,
  rawText: string,
  parsed: any,
): LegacyDataContainerState {
  return {
    id: LEGACY_DATA_CONTAINER_IDS[kind],
    rawText,
    parsed,
    consumed: false,
    reason: 'boot-payload',
    updatedAt: 0,
  }
}

/**
 * Parse a nextjs=1 boot payload into a renderable per-request SubsStoreState.
 * Returns null when the payload carries no msges data span (locked/error/
 * to_link/php-model payloads) — the caller falls back to the non-SSR flow.
 */
export function payloadToState(
  payloadHtml: string,
  options: PayloadToStateOptions = {},
): SubsStoreState | null {
  const msgesJson = extractPayloadSpanJson(payloadHtml, LEGACY_DATA_CONTAINER_IDS.msges)
    || extractPayloadSpanJson(payloadHtml, LEGACY_DATA_CONTAINER_IDS.msgesDataJson)
  const msges = parseJson(msgesJson)
  if (!msges || typeof msges !== 'object' || !msges.subs) return null

  const initJson = extractPayloadSpanJson(payloadHtml, LEGACY_DATA_CONTAINER_IDS.init)
  const init = parseJson(initJson)

  const mode: DeskBootMode = options.mode || 'msg'
  const state = emptySubsStoreState()

  // Merge folder/tab section spans (already-fetched load_more_subs responses)
  // into the same subs map; their trees own that section's root ids.
  const subs: Record<string, any> = {}
  const sourceSubs = msges.subs
  if (Array.isArray(sourceSubs)) {
    sourceSubs.forEach((sub: any, index: number) => {
      if (sub && typeof sub === 'object') subs[index] = sub
    })
  } else if (sourceSubs && typeof sourceSubs === 'object') {
    Object.assign(subs, sourceSubs)
  }

  const sectionRootIds: Record<SubsSection, number[]> = {
    answers: [],
    submsges: [],
  }
  const bootTree = Array.isArray(msges.subs_tree) ? msges.subs_tree : [[], []]
  const bootTreeProjection = projectLegacySubsTree(bootTree)
  state.childrenMap = bootTreeProjection.childrenMap

  if (mode === 'msg' || mode === 'answers' || mode === 'submsges') {
    if (mode !== 'submsges') sectionRootIds.answers = bootTreeProjection.rootIds.answers
    if (mode !== 'answers') sectionRootIds.submsges = bootTreeProjection.rootIds.submsges
  }

  const sectionSpansSeen: SubsSection[] = []
  for (const span of options.sectionSpans || []) {
    const spanJson = extractPayloadSpanJson(span.html, LEGACY_DATA_CONTAINER_IDS.msges)
      || extractPayloadSpanJson(span.html, LEGACY_DATA_CONTAINER_IDS.msgesDataJson)
    const spanData = parseJson(spanJson)
    if (!spanData || typeof spanData !== 'object' || !spanData.subs) continue
    if (spanData.subs && typeof spanData.subs === 'object' && !Array.isArray(spanData.subs)) {
      Object.assign(subs, spanData.subs)
    }
    const spanTree = Array.isArray(spanData.subs_tree) ? spanData.subs_tree : [[], []]
    const spanTreeProjection = projectLegacySubsTree(spanTree)
    state.childrenMap = mergeLegacyChildrenMaps(state.childrenMap, spanTreeProjection.childrenMap)
    const ids = spanTreeProjection.rootIds[span.section]
    if (ids.length) {
      sectionRootIds[span.section] = ids
      sectionSpansSeen.push(span.section)
    }
  }

  const auxiliaryRootIds: Record<string, Partial<Record<SubsSection, number[]>>> = {}
  for (const span of options.collectionSpans || []) {
    const spanJson = extractPayloadSpanJson(span.html, LEGACY_DATA_CONTAINER_IDS.msges)
      || extractPayloadSpanJson(span.html, LEGACY_DATA_CONTAINER_IDS.msgesDataJson)
    const spanData = parseJson(spanJson)
    if (!spanData || typeof spanData !== 'object' || !spanData.subs) continue
    if (spanData.subs && typeof spanData.subs === 'object' && !Array.isArray(spanData.subs)) {
      Object.assign(subs, spanData.subs)
    }
    const spanTree = Array.isArray(spanData.subs_tree) ? spanData.subs_tree : [[], []]
    const spanTreeProjection = projectLegacySubsTree(spanTree)
    state.childrenMap = mergeLegacyChildrenMaps(state.childrenMap, spanTreeProjection.childrenMap)
    const ids = spanTreeProjection.rootIds[span.section]
    if (ids.length) {
      auxiliaryRootIds[span.mode] = {
        ...(auxiliaryRootIds[span.mode] || {}),
        [span.section]: ids,
      }
    }
  }

  const activeSection: SubsSection = options.activeSection
    || (mode === 'submsges' ? 'submsges' : sectionSpansSeen[0] || 'answers')

  state.legacyData = {
    ...(initJson ? { initJson, init } : {}),
    msges: { ...msges, subs },
    containers: {
      ...(initJson ? { init: containerState('init', initJson, init) } : {}),
      ...(msgesJson ? { msges: containerState('msges', msgesJson, null) } : {}),
    },
  }
  state.renderProjection = {
    rootIds: sectionRootIds,
    currentPage: {},
    mode,
    activeSection,
  }
  state.auxiliaryRootIds = auxiliaryRootIds
  state.chromeByContract = chromeSeedsForMode(mode)
  state.pooProtocol = options.pooProtocol
  state.syncVersion = 1

  return state
}
